import type { NodeData, Rect, Vec2, ShapeKind } from './types.ts';

// ---------------------------------------------------------------------------
// 2D affine matrices: [a, b, c, d, e, f] maps (x, y) -> (a x + c y + e, b x + d y + f)
// ---------------------------------------------------------------------------

export type Mat = [number, number, number, number, number, number];

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

export function mul(m: Mat, n: Mat): Mat {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function invert(m: Mat): Mat {
  const det = m[0] * m[3] - m[1] * m[2];
  if (Math.abs(det) < 1e-12) return [...IDENTITY];
  const id = 1 / det;
  return [
    m[3] * id,
    -m[1] * id,
    -m[2] * id,
    m[0] * id,
    (m[2] * m[5] - m[3] * m[4]) * id,
    (m[1] * m[4] - m[0] * m[5]) * id,
  ];
}

export function apply(m: Mat, p: Vec2): Vec2 {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

export function translate(x: number, y: number): Mat {
  return [1, 0, 0, 1, x, y];
}

export function rotate(deg: number): Mat {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [c, s, -s, c, 0, 0];
}

export function scale(sx: number, sy = sx): Mat {
  return [sx, 0, 0, sy, 0, 0];
}

/** Transform from node-local space (0..w, 0..h) into the parent's space. */
export function localTransform(n: Pick<NodeData, 'x' | 'y' | 'width' | 'height' | 'rotation' | 'flipX' | 'flipY'>): Mat {
  const hasFlip = n.flipX || n.flipY;
  if (!n.rotation && !hasFlip) return translate(n.x, n.y);
  const cx = n.width / 2;
  const cy = n.height / 2;
  let m = translate(n.x + cx, n.y + cy);
  if (n.rotation) m = mul(m, rotate(n.rotation));
  if (hasFlip) m = mul(m, scale(n.flipX ? -1 : 1, n.flipY ? -1 : 1));
  return mul(m, translate(-cx, -cy));
}

export function transformRect(m: Mat, r: Rect): Rect {
  const pts = [
    apply(m, { x: r.x, y: r.y }),
    apply(m, { x: r.x + r.width, y: r.y }),
    apply(m, { x: r.x + r.width, y: r.y + r.height }),
    apply(m, { x: r.x, y: r.y + r.height }),
  ];
  return boundsOfPoints(pts);
}

export function boundsOfPoints(pts: Vec2[]): Rect {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of pts) {
    if (p.x < x0) x0 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.x > x1) x1 = p.x;
    if (p.y > y1) y1 = p.y;
  }
  if (x0 === Infinity) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

export function unionRect(a: Rect | null, b: Rect): Rect {
  if (!a) return { ...b };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

export function intersects(a: Rect, b: Rect): boolean {
  return a.x <= b.x + b.width && b.x <= a.x + a.width && a.y <= b.y + b.height && b.y <= a.y + a.height;
}

export function containsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

export function expandRect(r: Rect, d: number): Rect {
  return { x: r.x - d, y: r.y - d, width: r.width + 2 * d, height: r.height + 2 * d };
}

export function rectCenter(r: Rect): Vec2 {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

// ---------------------------------------------------------------------------
// Paths. Internally a path is a list of absolute commands using only M, L, C, Z.
// ---------------------------------------------------------------------------

export type PathCmd =
  | { c: 'M'; x: number; y: number }
  | { c: 'L'; x: number; y: number }
  | { c: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { c: 'Z' };

export function parseSvgPath(d: string): PathCmd[] {
  const out: PathCmd[] = [];
  const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/g) ?? [];
  let i = 0;
  let cmd = '';
  let cx = 0,
    cy = 0,
    sx = 0,
    sy = 0;
  let lastCtrl: Vec2 | null = null;
  let lastQuad: Vec2 | null = null;
  const num = () => parseFloat(tokens[i++]);
  const isNum = () => i < tokens.length && !/^[a-zA-Z]$/.test(tokens[i]);
  while (i < tokens.length) {
    if (/^[a-zA-Z]$/.test(tokens[i])) cmd = tokens[i++];
    else if (!cmd) {
      i++;
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    const ox = rel ? cx : 0;
    const oy = rel ? cy : 0;
    switch (C) {
      case 'M': {
        cx = ox + num();
        cy = oy + num();
        sx = cx;
        sy = cy;
        out.push({ c: 'M', x: cx, y: cy });
        cmd = rel ? 'l' : 'L';
        lastCtrl = lastQuad = null;
        break;
      }
      case 'L':
        cx = ox + num();
        cy = oy + num();
        out.push({ c: 'L', x: cx, y: cy });
        lastCtrl = lastQuad = null;
        break;
      case 'H':
        cx = (rel ? cx : 0) + num();
        out.push({ c: 'L', x: cx, y: cy });
        lastCtrl = lastQuad = null;
        break;
      case 'V':
        cy = (rel ? cy : 0) + num();
        out.push({ c: 'L', x: cx, y: cy });
        lastCtrl = lastQuad = null;
        break;
      case 'C': {
        const x1 = ox + num(),
          y1 = oy + num(),
          x2 = ox + num(),
          y2 = oy + num();
        cx = ox + num();
        cy = oy + num();
        out.push({ c: 'C', x1, y1, x2, y2, x: cx, y: cy });
        lastCtrl = { x: x2, y: y2 };
        lastQuad = null;
        break;
      }
      case 'S': {
        const x1 = lastCtrl ? 2 * cx - lastCtrl.x : cx;
        const y1 = lastCtrl ? 2 * cy - lastCtrl.y : cy;
        const x2 = ox + num(),
          y2 = oy + num();
        cx = ox + num();
        cy = oy + num();
        out.push({ c: 'C', x1, y1, x2, y2, x: cx, y: cy });
        lastCtrl = { x: x2, y: y2 };
        lastQuad = null;
        break;
      }
      case 'Q':
      case 'T': {
        let qx: number, qy: number;
        if (C === 'Q') {
          qx = ox + num();
          qy = oy + num();
        } else {
          qx = lastQuad ? 2 * cx - lastQuad.x : cx;
          qy = lastQuad ? 2 * cy - lastQuad.y : cy;
        }
        const x = ox + num(),
          y = oy + num();
        out.push({
          c: 'C',
          x1: cx + (2 / 3) * (qx - cx),
          y1: cy + (2 / 3) * (qy - cy),
          x2: x + (2 / 3) * (qx - x),
          y2: y + (2 / 3) * (qy - y),
          x,
          y,
        });
        cx = x;
        cy = y;
        lastQuad = { x: qx, y: qy };
        lastCtrl = null;
        break;
      }
      case 'A': {
        const rx = num(),
          ry = num(),
          rotDeg = num(),
          large = num(),
          sweep = num();
        const x = ox + num(),
          y = oy + num();
        for (const seg of arcToCubics(cx, cy, rx, ry, rotDeg, !!large, !!sweep, x, y)) out.push(seg);
        cx = x;
        cy = y;
        lastCtrl = lastQuad = null;
        break;
      }
      case 'Z':
        out.push({ c: 'Z' });
        cx = sx;
        cy = sy;
        lastCtrl = lastQuad = null;
        cmd = ''; // stray numbers after Z are ignored
        break;
      default:
        // Unknown command: skip its numbers.
        while (isNum()) i++;
    }
  }
  return out;
}

function arcToCubics(
  x1: number,
  y1: number,
  rx: number,
  ry: number,
  phiDeg: number,
  largeArc: boolean,
  sweep: boolean,
  x2: number,
  y2: number,
): PathCmd[] {
  if (rx === 0 || ry === 0) return [{ c: 'L', x: x2, y: y2 }];
  const phi = (phiDeg * Math.PI) / 180;
  const cos = Math.cos(phi),
    sin = Math.sin(phi);
  const dx = (x1 - x2) / 2,
    dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy;
  const y1p = -sin * dx + cos * dy;
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const sign = largeArc === sweep ? -1 : 1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const coef = sign * Math.sqrt(Math.max(0, num / den));
  const cxp = (coef * (rx * y1p)) / ry;
  const cyp = (coef * -(ry * x1p)) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2;
  const cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const ang = (ux: number, uy: number, vx: number, vy: number) => {
    const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return a;
  };
  const t1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dt = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dt > 0) dt -= 2 * Math.PI;
  if (sweep && dt < 0) dt += 2 * Math.PI;
  const segs = Math.ceil(Math.abs(dt) / (Math.PI / 2));
  const out: PathCmd[] = [];
  const delta = dt / segs;
  const k = (4 / 3) * Math.tan(delta / 4);
  let t = t1;
  for (let s = 0; s < segs; s++) {
    const c1 = Math.cos(t),
      s1 = Math.sin(t),
      c2 = Math.cos(t + delta),
      s2 = Math.sin(t + delta);
    const p = (ex: number, ey: number) => ({ x: cx + rx * ex * cos - ry * ey * sin, y: cy + rx * ex * sin + ry * ey * cos });
    const a = p(c1 - k * s1, s1 + k * c1);
    const b = p(c2 + k * s2, s2 - k * c2);
    const e = p(c2, s2);
    out.push({ c: 'C', x1: a.x, y1: a.y, x2: b.x, y2: b.y, x: e.x, y: e.y });
    t += delta;
  }
  return out;
}

export function pathToSvg(cmds: PathCmd[]): string {
  const f = (n: number) => +n.toFixed(3);
  return cmds
    .map((c) => {
      switch (c.c) {
        case 'M':
          return `M${f(c.x)} ${f(c.y)}`;
        case 'L':
          return `L${f(c.x)} ${f(c.y)}`;
        case 'C':
          return `C${f(c.x1)} ${f(c.y1)} ${f(c.x2)} ${f(c.y2)} ${f(c.x)} ${f(c.y)}`;
        case 'Z':
          return 'Z';
      }
    })
    .join('');
}

export function transformPath(cmds: PathCmd[], m: Mat): PathCmd[] {
  return cmds.map((c) => {
    if (c.c === 'Z') return c;
    if (c.c === 'C') {
      const a = apply(m, { x: c.x1, y: c.y1 });
      const b = apply(m, { x: c.x2, y: c.y2 });
      const e = apply(m, { x: c.x, y: c.y });
      return { c: 'C', x1: a.x, y1: a.y, x2: b.x, y2: b.y, x: e.x, y: e.y };
    }
    const p = apply(m, c);
    return { c: c.c, x: p.x, y: p.y };
  });
}

/** Flatten a path into polylines (one per subpath). tolerance in path units. */
export function flattenPath(cmds: PathCmd[], tolerance = 0.25): { points: Vec2[]; closed: boolean }[] {
  const out: { points: Vec2[]; closed: boolean }[] = [];
  let cur: Vec2[] | null = null;
  let px = 0,
    py = 0;
  const start = () => {
    if (cur && cur.length > 1) out.push({ points: cur, closed: false });
  };
  for (const c of cmds) {
    if (c.c === 'M') {
      start();
      cur = [{ x: c.x, y: c.y }];
      px = c.x;
      py = c.y;
    } else if (c.c === 'L') {
      if (!cur) cur = [{ x: px, y: py }];
      cur.push({ x: c.x, y: c.y });
      px = c.x;
      py = c.y;
    } else if (c.c === 'C') {
      if (!cur) cur = [{ x: px, y: py }];
      const dd =
        Math.hypot(c.x1 - px, c.y1 - py) + Math.hypot(c.x2 - c.x1, c.y2 - c.y1) + Math.hypot(c.x - c.x2, c.y - c.y2);
      const n = Math.max(2, Math.min(256, Math.ceil(Math.sqrt(dd / tolerance) * 1.2)));
      for (let i = 1; i <= n; i++) {
        const t = i / n,
          mt = 1 - t;
        const a = mt * mt * mt,
          b = 3 * mt * mt * t,
          cc = 3 * mt * t * t,
          d = t * t * t;
        cur.push({ x: a * px + b * c.x1 + cc * c.x2 + d * c.x, y: a * py + b * c.y1 + cc * c.y2 + d * c.y });
      }
      px = c.x;
      py = c.y;
    } else if (c.c === 'Z') {
      if (cur && cur.length > 1) {
        out.push({ points: cur, closed: true });
        px = cur[0].x;
        py = cur[0].y;
      }
      cur = null;
    }
  }
  start();
  return out;
}

export function pathBounds(cmds: PathCmd[]): Rect {
  const pts: Vec2[] = [];
  for (const sp of flattenPath(cmds, 1)) pts.push(...sp.points);
  return boundsOfPoints(pts);
}

// ---------------------------------------------------------------------------
// Shape outlines in node-local coordinates.
// ---------------------------------------------------------------------------

const K = 0.5522847498;

export function ellipsePath(x: number, y: number, w: number, h: number): PathCmd[] {
  const rx = w / 2,
    ry = h / 2,
    cx = x + rx,
    cy = y + ry;
  return [
    { c: 'M', x: cx + rx, y: cy },
    { c: 'C', x1: cx + rx, y1: cy + K * ry, x2: cx + K * rx, y2: cy + ry, x: cx, y: cy + ry },
    { c: 'C', x1: cx - K * rx, y1: cy + ry, x2: cx - rx, y2: cy + K * ry, x: cx - rx, y: cy },
    { c: 'C', x1: cx - rx, y1: cy - K * ry, x2: cx - K * rx, y2: cy - ry, x: cx, y: cy - ry },
    { c: 'C', x1: cx + K * rx, y1: cy - ry, x2: cx + rx, y2: cy - K * ry, x: cx + rx, y: cy },
    { c: 'Z' },
  ];
}

export function cornerRadii(n: Pick<NodeData, 'cornerRadius' | 'width' | 'height'>): [number, number, number, number] {
  const r = n.cornerRadius;
  const arr: [number, number, number, number] = Array.isArray(r) ? [...r] : [r, r, r, r];
  const max = Math.min(Math.abs(n.width), Math.abs(n.height)) / 2;
  return arr.map((v) => Math.max(0, Math.min(v || 0, max))) as [number, number, number, number];
}

export function roundedRectPath(x: number, y: number, w: number, h: number, radii: [number, number, number, number]): PathCmd[] {
  const [tl, tr, br, bl] = radii;
  if (!tl && !tr && !br && !bl) {
    return [
      { c: 'M', x, y },
      { c: 'L', x: x + w, y },
      { c: 'L', x: x + w, y: y + h },
      { c: 'L', x, y: y + h },
      { c: 'Z' },
    ];
  }
  const out: PathCmd[] = [{ c: 'M', x: x + tl, y }];
  out.push({ c: 'L', x: x + w - tr, y });
  if (tr) out.push({ c: 'C', x1: x + w - tr + K * tr, y1: y, x2: x + w, y2: y + tr - K * tr, x: x + w, y: y + tr });
  out.push({ c: 'L', x: x + w, y: y + h - br });
  if (br) out.push({ c: 'C', x1: x + w, y1: y + h - br + K * br, x2: x + w - br + K * br, y2: y + h, x: x + w - br, y: y + h });
  out.push({ c: 'L', x: x + bl, y: y + h });
  if (bl) out.push({ c: 'C', x1: x + bl - K * bl, y1: y + h, x2: x, y2: y + h - bl + K * bl, x, y: y + h - bl });
  out.push({ c: 'L', x, y: y + tl });
  if (tl) out.push({ c: 'C', x1: x, y1: y + tl - K * tl, x2: x + tl - K * tl, y2: y, x: x + tl, y });
  out.push({ c: 'Z' });
  return out;
}

function polyPath(pts: Vec2[]): PathCmd[] {
  const out: PathCmd[] = pts.map((p, i) => ({ c: i === 0 ? 'M' : 'L', x: p.x, y: p.y }) as PathCmd);
  out.push({ c: 'Z' });
  return out;
}

export function polygonPath(w: number, h: number, count: number): PathCmd[] {
  const n = Math.max(3, Math.round(count));
  const pts: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    pts.push({ x: w / 2 + (Math.cos(a) * w) / 2, y: h / 2 + (Math.sin(a) * h) / 2 });
  }
  return polyPath(pts);
}

export function starPath(w: number, h: number, count: number, inner: number): PathCmd[] {
  const n = Math.max(3, Math.round(count));
  const pts: Vec2[] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / n;
    const r = i % 2 === 0 ? 1 : inner;
    pts.push({ x: w / 2 + (Math.cos(a) * w * r) / 2, y: h / 2 + (Math.sin(a) * h * r) / 2 });
  }
  return polyPath(pts);
}

/** A closed polygon whose corners are rounded by cutting `r` along each edge (FigJam's shapes). */
function roundedPoly(pts: Vec2[], r: number): PathCmd[] {
  if (r <= 0) return polyPath(pts);
  const n = pts.length;
  const out: PathCmd[] = [];
  for (let i = 0; i < n; i++) {
    const v = pts[i],
      p = pts[(i + n - 1) % n],
      q = pts[(i + 1) % n];
    const lp = Math.hypot(p.x - v.x, p.y - v.y),
      lq = Math.hypot(q.x - v.x, q.y - v.y);
    // the same cut at every corner matched FigJam best (against true fillets and tan(θ/2) cuts)
    const t = Math.min(r, lp / 2, lq / 2);
    const a = { x: v.x + ((p.x - v.x) * t) / (lp || 1), y: v.y + ((p.y - v.y) * t) / (lp || 1) };
    const b = { x: v.x + ((q.x - v.x) * t) / (lq || 1), y: v.y + ((q.y - v.y) * t) / (lq || 1) };
    out.push({ c: i === 0 ? 'M' : 'L', x: a.x, y: a.y });
    out.push({ c: 'C', x1: a.x + (v.x - a.x) * K, y1: a.y + (v.y - a.y) * K, x2: b.x + (v.x - b.x) * K, y2: b.y + (v.y - b.y) * K, x: b.x, y: b.y });
  }
  out.push({ c: 'Z' });
  return out;
}

/** FigJam's corner rounding on its polygonal shapes (read back from FigJam: cornerRadius 6). */
const WB_CORNER = 6;

/** Arrow geometry, as FigJam draws it: the head is up to 1.3× as long as the shape is tall. */
function arrowHead(w: number, h: number) {
  return Math.min(h * 1.3, w * 0.93);
}

/**
 * FigJam's shapes, measured against FigJam's renders: polygons have 6px rounded corners, rounded
 * rectangles a 32px radius, cylinders a cap 0.24× the short side (with its front edge drawn).
 */
export function whiteboardShapePath(kind: ShapeKind, w: number, h: number): PathCmd[] {
  const r = Math.min(WB_CORNER, w / 4, h / 4);
  switch (kind) {
    case 'ELLIPSE':
      return ellipsePath(0, 0, w, h);
    case 'ROUNDED_RECTANGLE': {
      const rr = Math.min(32, w / 2, h / 2);
      return roundedRectPath(0, 0, w, h, [rr, rr, rr, rr]);
    }
    case 'DIAMOND':
      return roundedPoly([{ x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 }], r);
    case 'TRIANGLE_UP':
      return roundedPoly([{ x: w / 2, y: 0 }, { x: w, y: h }, { x: 0, y: h }], r);
    case 'TRIANGLE_DOWN':
      return roundedPoly([{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w / 2, y: h }], r);
    case 'PARALLELOGRAM': {
      const o = w * 0.2;
      return roundedPoly([{ x: o, y: 0 }, { x: w, y: 0 }, { x: w - o, y: h }, { x: 0, y: h }], r);
    }
    case 'HEXAGON': {
      const o = w * 0.12;
      return roundedPoly([{ x: o, y: 0 }, { x: w - o, y: 0 }, { x: w, y: h / 2 }, { x: w - o, y: h }, { x: o, y: h }, { x: 0, y: h / 2 }], r);
    }
    case 'STAR':
      return starPath(w, h, 5, 0.45);
    case 'CYLINDER': {
      const e = Math.min(w, h) * 0.24;
      const k = K * e,
        hw = w / 2,
        kw = K * hw;
      return [
        { c: 'M', x: 0, y: e },
        { c: 'C', x1: 0, y1: e - k, x2: hw - kw, y2: 0, x: hw, y: 0 },
        { c: 'C', x1: hw + kw, y1: 0, x2: w, y2: e - k, x: w, y: e },
        { c: 'L', x: w, y: h - e },
        { c: 'C', x1: w, y1: h - e + k, x2: hw + kw, y2: h, x: hw, y: h },
        { c: 'C', x1: hw - kw, y1: h, x2: 0, y2: h - e + k, x: 0, y: h - e },
        { c: 'Z' },
        // the cap's front edge: out and back along the same curve, so it strokes but adds no area
        { c: 'M', x: 0, y: e },
        { c: 'C', x1: 0, y1: e + k, x2: hw - kw, y2: 2 * e, x: hw, y: 2 * e },
        { c: 'C', x1: hw + kw, y1: 2 * e, x2: w, y2: e + k, x: w, y: e },
        { c: 'C', x1: w, y1: e + k, x2: hw + kw, y2: 2 * e, x: hw, y: 2 * e },
        { c: 'C', x1: hw - kw, y1: 2 * e, x2: 0, y2: e + k, x: 0, y: e },
        { c: 'Z' },
      ];
    }
    case 'ARROW_RIGHT': {
      const b = w - arrowHead(w, h);
      return roundedPoly([{ x: 0, y: h * 0.25 }, { x: b, y: h * 0.25 }, { x: b, y: 0 }, { x: w, y: h / 2 }, { x: b, y: h }, { x: b, y: h * 0.75 }, { x: 0, y: h * 0.75 }], r);
    }
    case 'ARROW_LEFT': {
      const b = arrowHead(w, h);
      return roundedPoly([{ x: w, y: h * 0.25 }, { x: b, y: h * 0.25 }, { x: b, y: 0 }, { x: 0, y: h / 2 }, { x: b, y: h }, { x: b, y: h * 0.75 }, { x: w, y: h * 0.75 }], r);
    }
    case 'SQUARE':
    default:
      return roundedRectPath(0, 0, w, h, [r, r, r, r]);
  }
}

/**
 * Where a FigJam shape's text goes (node-local): the shape's largest inner rectangle, padded —
 * so a triangle's text sits low, a cylinder's below its cap, an arrow's inside its head.
 */
export function whiteboardTextBox(kind: ShapeKind, w: number, h: number): Rect {
  const pad = (b: Rect, p: number): Rect => ({ x: b.x + p, y: b.y + p / 2, width: Math.max(1, b.width - p * 2), height: Math.max(1, b.height - p) });
  switch (kind) {
    case 'ELLIPSE':
      return pad({ x: w * 0.1464, y: h * 0.1464, width: w * 0.7071, height: h * 0.7071 }, 4);
    case 'DIAMOND':
      return pad({ x: w / 4, y: h / 4, width: w / 2, height: h / 2 }, 8);
    case 'TRIANGLE_UP':
      return pad({ x: w / 4, y: h * 0.45, width: w / 2, height: h * 0.5 }, 4);
    case 'TRIANGLE_DOWN':
      return pad({ x: w / 4, y: h * 0.05, width: w / 2, height: h * 0.5 }, 4);
    case 'HEXAGON':
      return pad({ x: w * 0.12, y: 0, width: w * 0.76, height: h }, 8);
    case 'CYLINDER': {
      const e = Math.min(w, h) * 0.24;
      return pad({ x: 0, y: 2 * e, width: w, height: h - 2 * e }, 8);
    }
    case 'ARROW_RIGHT': {
      const head = arrowHead(w, h);
      return pad({ x: w - head, y: h * 0.2, width: head / 2, height: h * 0.6 }, 8);
    }
    case 'ARROW_LEFT': {
      const head = arrowHead(w, h);
      return pad({ x: head / 2, y: h * 0.2, width: head / 2, height: h * 0.6 }, 8);
    }
    case 'STAR':
      return pad({ x: w * 0.3, y: h * 0.35, width: w * 0.4, height: h * 0.4 }, 4);
    default: {
      const p = Math.min(16, w * 0.1);
      return { x: p, y: p, width: Math.max(1, w - p * 2), height: Math.max(1, h - p * 2) };
    }
  }
}

/**
 * Dashes on closed outlines, the way Figma lays them out: every segment of the outline (an edge
 * or a corner curve) gets a whole number of pattern periods, with a dash centred on each of its
 * ends; dash lengths stay and the gaps stretch to fit. So corners always carry a dash and both
 * ends of an edge look alike. Returns the dashes as open polylines, or null for open paths
 * (those keep the plain pattern).
 */
export function dashOutline(cmds: PathCmd[], pattern: number[], tolerance = 0.1): Vec2[][] | null {
  // an odd pattern repeats to an even one, as in SVG
  const pat = pattern.length % 2 ? [...pattern, ...pattern] : pattern;
  const dashes = pat.filter((_, i) => i % 2 === 0),
    gaps = pat.filter((_, i) => i % 2 === 1);
  const D = dashes.reduce((a, b) => a + b, 0),
    G = gaps.reduce((a, b) => a + b, 0);
  if (D <= 0 || D + G <= 0) return null;
  const out: Vec2[][] = [];
  // split into closed subpaths of pieces (one per command), each flattened
  let pieces: Vec2[][] = [];
  let start: Vec2 | null = null,
    cur: Vec2 = { x: 0, y: 0 },
    closed = false;
  const flush = () => {
    if (pieces.length && closed) dashLoop(pieces, dashes, gaps, D, G, out);
    else if (pieces.length) return false;
    pieces = [];
    return true;
  };
  for (const c of cmds) {
    if (c.c === 'M') {
      if (!flush()) return null;
      start = cur = { x: c.x, y: c.y };
      closed = false;
    } else if (c.c === 'L') {
      pieces.push([cur, { x: c.x, y: c.y }]);
      cur = { x: c.x, y: c.y };
    } else if (c.c === 'C') {
      const n = Math.max(2, Math.ceil(Math.hypot(c.x1 - cur.x, c.y1 - cur.y) + Math.hypot(c.x2 - c.x1, c.y2 - c.y1) + Math.hypot(c.x - c.x2, c.y - c.y2)) / Math.max(0.5, tolerance * 8));
      const pts: Vec2[] = [cur];
      for (let i = 1; i <= n; i++) {
        const t = i / n,
          u = 1 - t;
        pts.push({ x: u * u * u * cur.x + 3 * u * u * t * c.x1 + 3 * u * t * t * c.x2 + t * t * t * c.x, y: u * u * u * cur.y + 3 * u * u * t * c.y1 + 3 * u * t * t * c.y2 + t * t * t * c.y });
      }
      pieces.push(pts);
      cur = { x: c.x, y: c.y };
    } else if (c.c === 'Z') {
      if (start && Math.hypot(cur.x - start.x, cur.y - start.y) > 1e-6) pieces.push([cur, start]);
      closed = true;
      if (!flush()) return null;
      if (start) cur = start;
      start = null;
    }
  }
  if (pieces.length) return null; // an unclosed subpath
  return out;
}

function dashLoop(pieces: Vec2[][], dashes: number[], gaps: number[], D: number, G: number, out: Vec2[][]) {
  const lens = pieces.map((p) => p.slice(1).reduce((s, q, i) => s + Math.hypot(q.x - p[i].x, q.y - p[i].y), 0));
  const total = lens.reduce((a, b) => a + b, 0);
  if (total <= 0) return;
  // dash intervals in loop arc length
  const iv: [number, number][] = [];
  let s0 = 0;
  for (let k = 0; k < pieces.length; k++) {
    const L = lens[k];
    if (L <= 0) continue;
    const n = Math.max(1, Math.round(L / (D + G)));
    const stretch = G > 0 ? Math.max(0, L / n - D) / G : 0;
    // one period: dash0 centred on 0, then gap0, dash1, gap1…
    const offs: [number, number][] = [];
    let at = -dashes[0] / 2;
    dashes.forEach((d, j) => {
      offs.push([at, at + d]);
      at += d + (gaps[j] ?? 0) * stretch;
    });
    for (let p = 0; p <= n; p++)
      for (const [a, b] of offs) {
        const lo = Math.max(0, a + (p * L) / n),
          hi = Math.min(L, b + (p * L) / n);
        if (hi > lo + 1e-9) iv.push([s0 + lo, s0 + hi]);
      }
    s0 += L;
  }
  iv.sort((a, b) => a[0] - b[0]);
  // pieces of one dash meet at segment ends: join them (and across the loop's start)
  const merged: [number, number][] = [];
  for (const r of iv) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + 1e-6) last[1] = Math.max(last[1], r[1]);
    else merged.push([...r]);
  }
  if (merged.length > 1 && merged[0][0] <= 1e-6 && merged[merged.length - 1][1] >= total - 1e-6) {
    const tail = merged.pop()!;
    merged[0] = [tail[0] - total, merged[0][1]];
  }
  // the loop as one polyline with arc lengths, walked twice so wrapped dashes are continuous
  const pts: Vec2[] = [pieces[0][0]];
  for (const p of pieces) pts.push(...p.slice(1));
  const acc = [0];
  for (let i = 1; i < pts.length; i++) acc.push(acc[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const at = (s: number) => {
    s = ((s % total) + total) % total;
    let i = 1;
    while (i < acc.length - 1 && acc[i] < s) i++;
    const t = acc[i] > acc[i - 1] ? (s - acc[i - 1]) / (acc[i] - acc[i - 1]) : 0;
    return { i, p: { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * t, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * t } };
  };
  for (const [a, b] of merged) {
    if (b - a >= total - 1e-6) {
      out.push([...pts]);
      continue;
    }
    const line: Vec2[] = [at(a).p];
    // vertices strictly inside (a, b), going round the loop if needed
    for (let lap = 0; lap < 2; lap++)
      for (let i = 1; i < pts.length; i++) {
        const s = acc[i] + lap * total - (a < 0 ? total : 0);
        if (s > a + 1e-9 && s < b - 1e-9) line.push(pts[i]);
      }
    line.push(at(b).p);
    out.push(line);
  }
}

/**
 * Where a LINE's stroke runs, as Figma draws it (measured against Figma's renders): the stroke
 * sits on the side above the line (local y from −weight to 0, though Figma reports it as centred),
 * and round or square caps stay inside the line's length.
 */
export function lineSegment(n: { width: number; strokeWeight?: number; strokeCap?: string }): { a: Vec2; b: Vec2 } {
  const w = n.strokeWeight || 0;
  const inset = n.strokeCap === 'ROUND' || n.strokeCap === 'SQUARE' ? Math.min(w / 2, n.width / 2) : 0;
  return { a: { x: inset, y: -w / 2 }, b: { x: n.width - inset, y: -w / 2 } };
}

/** An ellipse that isn't a whole, solid one (an arc, pie or donut). */
export function hasArc(n: { type: string; arcData?: { startingAngle: number; endingAngle: number; innerRadius: number } }) {
  const a = n.type === 'ELLIPSE' ? n.arcData : undefined;
  return !!a && (a.innerRadius > 0 || Math.abs(a.endingAngle - a.startingAngle) < Math.PI * 2 - 1e-6);
}

/** Elliptical arc from angle a0 to a1 (clockwise, radians) as cubics, starting with a line or move to its start. */
function arcSegments(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, first: 'M' | 'L', out: PathCmd[]) {
  const sweep = a1 - a0;
  const n = Math.max(1, Math.ceil(Math.abs(sweep) / (Math.PI / 2) - 1e-9));
  const step = sweep / n;
  const k = (4 / 3) * Math.tan(step / 4);
  out.push({ c: first, x: cx + rx * Math.cos(a0), y: cy + ry * Math.sin(a0) });
  for (let i = 0; i < n; i++) {
    const t0 = a0 + i * step,
      t1 = t0 + step;
    out.push({
      c: 'C',
      x1: cx + rx * (Math.cos(t0) - k * Math.sin(t0)),
      y1: cy + ry * (Math.sin(t0) + k * Math.cos(t0)),
      x2: cx + rx * (Math.cos(t1) + k * Math.sin(t1)),
      y2: cy + ry * (Math.sin(t1) - k * Math.cos(t1)),
      x: cx + rx * Math.cos(t1),
      y: cy + ry * Math.sin(t1),
    });
  }
}

/** Figma's ellipse arcs: a pie (inner 0), a ring segment, or a full donut. */
export function arcPath(w: number, h: number, arc: { startingAngle: number; endingAngle: number; innerRadius: number }): PathCmd[] {
  const rx = w / 2,
    ry = h / 2;
  const a0 = arc.startingAngle;
  let a1 = arc.endingAngle;
  if (a1 < a0) a1 += Math.PI * 2;
  const full = a1 - a0 >= Math.PI * 2 - 1e-6;
  if (full) a1 = a0 + Math.PI * 2;
  const ir = Math.max(0, Math.min(1, arc.innerRadius));
  const out: PathCmd[] = [];
  arcSegments(rx, ry, rx, ry, a0, a1, 'M', out);
  if (full) {
    out.push({ c: 'Z' });
    // the hole runs the other way, so it cuts out under nonzero filling too
    if (ir > 0) {
      arcSegments(rx, ry, rx * ir, ry * ir, a1, a0, 'M', out);
      out.push({ c: 'Z' });
    }
    return out;
  }
  if (ir > 0) arcSegments(rx, ry, rx * ir, ry * ir, a1, a0, 'L', out);
  else out.push({ c: 'L', x: rx, y: ry });
  out.push({ c: 'Z' });
  return out;
}

/** Geometry outline for any node type, in node-local coordinates. Null = not a closed shape (text, containers without fill). */
export function nodeOutline(n: NodeData): PathCmd[] | null {
  switch (n.type) {
    case 'ELLIPSE':
      return hasArc(n) ? arcPath(n.width, n.height, n.arcData!) : ellipsePath(0, 0, n.width, n.height);
    case 'POLYGON':
      return polygonPath(n.width, n.height, n.pointCount ?? 3);
    case 'STAR':
      return starPath(n.width, n.height, n.pointCount ?? 5, n.innerRadius ?? 0.382);
    case 'VECTOR':
      return n.path ? parseSvgPath(n.path) : [];
    case 'LINE': {
      const { a, b } = lineSegment(n);
      return [
        { c: 'M', x: a.x, y: a.y },
        { c: 'L', x: b.x, y: b.y },
      ];
    }
    case 'SHAPE_WITH_TEXT':
      return whiteboardShapePath(n.shape ?? 'SQUARE', n.width, n.height);
    default: {
      const radii = cornerRadii(n);
      if (n.cornerSmoothing && radii.some((r) => r > 0)) return smoothRectPath(n.width, n.height, radii, n.cornerSmoothing);
      return roundedRectPath(0, 0, n.width, n.height, radii);
    }
  }
}

/** Per-side stroke weights in effect, or null for a uniform stroke. */
export function sideWeights(n: Pick<NodeData, 'type' | 'strokeWeights'>): [number, number, number, number] | null {
  if (!n.strokeWeights) return null;
  if (n.type !== 'RECTANGLE' && n.type !== 'FRAME' && n.type !== 'COMPONENT' && n.type !== 'INSTANCE' && n.type !== 'IMAGE') return null;
  return n.strokeWeights;
}

/**
 * The area a per-side stroke covers: an outer and an inner rounded rectangle (fill with even-odd).
 * Inside strokes grow inward from the edge, outside strokes outward, center strokes both ways.
 */
export function sideStrokeRing(n: NodeData): PathCmd[] {
  const [t, r, b, l] = sideWeights(n) ?? [n.strokeWeight, n.strokeWeight, n.strokeWeight, n.strokeWeight];
  const k = n.strokeAlign === 'INSIDE' ? 0 : n.strokeAlign === 'OUTSIDE' ? 1 : 0.5;
  const out = [t * k, r * k, b * k, l * k],
    inn = [t * (1 - k), r * (1 - k), b * (1 - k), l * (1 - k)];
  const [rtl, rtr, rbr, rbl] = cornerRadii(n);
  // a corner's radius grows (outer) or shrinks (inner) by the thicker of its two sides
  const grow = (rad: number, a: number, c: number) => (rad > 0 ? rad + Math.max(a, c) : 0);
  const shrink = (rad: number, a: number, c: number) => Math.max(0, rad - Math.max(a, c));
  const ow = n.width + out[1] + out[3],
    oh = n.height + out[0] + out[2];
  const iw = Math.max(0, n.width - inn[1] - inn[3]),
    ih = Math.max(0, n.height - inn[0] - inn[2]);
  const clampR = (rs: number[], w: number, h: number) => rs.map((v) => Math.min(v, w / 2, h / 2)) as [number, number, number, number];
  return [
    ...roundedRectPath(0 - out[3], 0 - out[0], ow, oh, clampR([grow(rtl, out[0], out[3]), grow(rtr, out[0], out[1]), grow(rbr, out[2], out[1]), grow(rbl, out[2], out[3])], ow, oh)),
    ...(iw > 0 && ih > 0 ? roundedRectPath(inn[3], inn[0], iw, ih, clampR([shrink(rtl, inn[0], inn[3]), shrink(rtr, inn[0], inn[1]), shrink(rbr, inn[2], inn[1]), shrink(rbl, inn[2], inn[3])], iw, ih)) : []),
  ];
}

/**
 * A rectangle with smoothed ("squircle") corners. Each corner starts further along its edges,
 * at (1 + s)·r instead of r, and eases in with a cubic curve, then a shorter circular arc
 * (90°·(1 − s)) and a mirrored cubic. A corner can't reach past half of its shorter edge, so
 * crowded corners lose smoothing before they lose radius.
 */
export function smoothRectPath(w: number, h: number, radii: [number, number, number, number], smoothing: number): PathCmd[] {
  const s0 = Math.max(0, Math.min(1, smoothing));
  const budget = Math.min(w, h) / 2;
  // corner origin, direction of the incoming edge, direction of the outgoing edge (clockwise)
  const corners: [Vec2, Vec2, Vec2][] = [
    [{ x: 0, y: 0 }, { x: 0, y: -1 }, { x: 1, y: 0 }],
    [{ x: w, y: 0 }, { x: 1, y: 0 }, { x: 0, y: 1 }],
    [{ x: w, y: h }, { x: 0, y: 1 }, { x: -1, y: 0 }],
    [{ x: 0, y: h }, { x: -1, y: 0 }, { x: 0, y: -1 }],
  ];
  const out: PathCmd[] = [];
  corners.forEach(([o, u, v], i) => {
    // canonical corner space: X runs along the incoming edge (≤ 0 before the corner), Y along the outgoing one
    const P = (X: number, Y: number) => ({ x: o.x + u.x * X + v.x * Y, y: o.y + u.y * X + v.y * Y });
    const r = Math.min(radii[i], budget);
    if (r <= 0) {
      const p = P(0, 0);
      out.push(i ? { c: 'L', ...p } : { c: 'M', ...p });
      return;
    }
    const p = Math.min((1 + s0) * r, budget);
    const s = p / r - 1; // smoothing that fits
    const deg = Math.PI / 180;
    const arc = 90 * (1 - s);
    const arcChord = Math.sin((arc / 2) * deg) * r * Math.SQRT2;
    const alpha = (90 - arc) / 2;
    const p3p4 = r * Math.tan((alpha / 2) * deg);
    const beta = 45 * s;
    const c = p3p4 * Math.cos(beta * deg);
    const d = c * Math.tan(beta * deg);
    const b = (p - arcChord - c - d) / 3;
    const a = 2 * b;
    const start = P(-p, 0);
    out.push(i ? { c: 'L', ...start } : { c: 'M', ...start });
    const e1 = P(-p + a + b + c, d);
    let q = P(-p + a, 0),
      q2 = P(-p + a + b, 0);
    out.push({ c: 'C', x1: q.x, y1: q.y, x2: q2.x, y2: q2.y, x: e1.x, y: e1.y });
    // the arc, centred at (−r, r), as one cubic
    const t1 = Math.atan2(d - r, -p + a + b + c + r),
      t2 = Math.atan2(p - a - b - c - r, -d + r);
    const k = (4 / 3) * Math.tan((t2 - t1) / 4) * r;
    const A = (t: number) => ({ X: -r + r * Math.cos(t), Y: r + r * Math.sin(t) });
    const a1 = A(t1),
      a2 = A(t2);
    q = P(a1.X - k * Math.sin(t1), a1.Y + k * Math.cos(t1));
    q2 = P(a2.X + k * Math.sin(t2), a2.Y - k * Math.cos(t2));
    const e2 = P(a2.X, a2.Y);
    out.push({ c: 'C', x1: q.x, y1: q.y, x2: q2.x, y2: q2.y, x: e2.x, y: e2.y });
    q = P(0, p - a - b);
    q2 = P(0, p - a);
    const end = P(0, p);
    out.push({ c: 'C', x1: q.x, y1: q.y, x2: q2.x, y2: q2.y, x: end.x, y: end.y });
  });
  out.push({ c: 'Z' });
  return out;
}

export function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  let t = l2 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Point-in-polygon test with winding rule over flattened subpaths. */
export function pointInPolylines(p: Vec2, polys: { points: Vec2[] }[], evenOdd = false): boolean {
  let winding = 0;
  for (const { points } of polys) {
    for (let i = 0, n = points.length; i < n; i++) {
      const a = points[i],
        b = points[(i + 1) % n];
      if (a.y <= p.y) {
        if (b.y > p.y && (b.x - a.x) * (p.y - a.y) - (p.x - a.x) * (b.y - a.y) > 0) winding++;
      } else if (b.y <= p.y && (b.x - a.x) * (p.y - a.y) - (p.x - a.x) * (b.y - a.y) < 0) winding--;
    }
  }
  return evenOdd ? winding % 2 !== 0 : winding !== 0;
}
